import { createSelector } from '@reduxjs/toolkit';
import { FeedEvent, Lease, Process, Unit } from '@urbanmix-tech/shared-js';

import { resolveUnitStatus, type ResolvedUnitStatus } from '@/helpers/unitStatus';
import type { RootState } from '@/store';

/**
 * Unit status, derived once per project rather than once per row.
 *
 * The fold itself is cheap, but doing it inline in a table body means
 * re-grouping the project's whole event list on every render. These selectors
 * group by unitId once and memoize on the three inputs that can actually change
 * it (units, events, leases).
 */

const selectUnits = (state: RootState) => state.project.projectUnits;
const selectLeases = (state: RootState) => state.project.projectLeases;
const selectEvents = (state: RootState) => state.project.unitStatusEvents;
const selectProcesses = (state: RootState) => state.project.processes;

/**
 * processId → process type.
 *
 * The native lease events (`lease_created`, `document.completed`, …) carry no
 * lease type, so this map is the only way the fold can tell a renewal from a new
 * lease. Without it every renewal renders as an incoming tenant.
 */
const selectProcessTypeById = createSelector([selectProcesses], (processes: Process[]) => {
  const byId: Record<string, string> = {};
  (processes ?? []).forEach((p) => {
    if (p?.id && p.type) byId[p.id] = p.type;
  });
  return byId;
});

function groupBy<T>(items: T[], key: (item: T) => string | undefined): Map<string, T[]> {
  const map = new Map<string, T[]>();
  items.forEach((item) => {
    const k = key(item);
    if (!k) return;
    const bucket = map.get(k);
    if (bucket) bucket.push(item);
    else map.set(k, [item]);
  });
  return map;
}

/** `unitId` → resolved status view, for every unit in the project. */
export const selectUnitStatusById = createSelector(
  [selectUnits, selectEvents, selectLeases, selectProcessTypeById],
  (
    units: Unit[],
    events: FeedEvent[],
    leases: Lease[],
    processTypeById: Record<string, string>
  ): Record<string, ResolvedUnitStatus> => {
    const eventsByUnit = groupBy(events ?? [], (e) => e.unitId);
    const leasesByUnit = groupBy(leases ?? [], (l) => l.unitId);

    const byId: Record<string, ResolvedUnitStatus> = {};
    (units ?? []).forEach((unit) => {
      byId[unit.id] = resolveUnitStatus(
        unit,
        eventsByUnit.get(unit.id) ?? [],
        leasesByUnit.get(unit.id) ?? [],
        { processTypeById }
      );
    });
    return byId;
  }
);

/** Project-wide occupancy counts, folded from the same source as the badges. */
export const selectOccupancyStats = createSelector([selectUnitStatusById], (byId) => {
  const statuses = Object.values(byId);
  const occupied = statuses.filter((s) => s.occupancy === 'occupied').length;
  return {
    total: statuses.length,
    occupied,
    vacant: statuses.length - occupied,
    /** How many units have real `state_*` events — i.e. backfill coverage. */
    withStatusEvents: statuses.filter((s) => s.hasStatusEvents).length,
  };
});
